import React from 'react'
import { CellProps, Row } from 'react-table'
import { get } from 'lodash'

import i18n from 'i18n'
import { isInternationalizationEnabled } from 'i18n/feature'
import { DateCell, NullableTextCell } from './cells'
import { TABLE_COLUMN_HEADER_I18N_KEYS } from './columnHeaderI18nKeys'
import map from 'lodash/map'

export const resolveColumnHeaderI18nKey = (column: { Header?: unknown; headerI18nKey?: string; id?: string }) => {
  if (column.headerI18nKey) {
    return column.headerI18nKey
  }
  if (typeof column.Header === 'string') {
    return TABLE_COLUMN_HEADER_I18N_KEYS[column.Header]
  }
}

export const translateColumnHeader = (column: { Header?: unknown; headerI18nKey?: string; id?: string }) => {
  if (!isInternationalizationEnabled()) {
    return undefined
  }
  const key = resolveColumnHeaderI18nKey(column)
  if (!key) {
    return undefined
  }
  const defaultValue = typeof column.Header === 'string' ? column.Header : column.id
  return i18n.t(`table.columns.${key}`, { ns: 'admin', defaultValue })
}

/** Header text for filters, chips, and column selector (string only). */
export const getColumnHeaderText = (column: any) => {
  const translated = translateColumnHeader(column)
  if (translated) {
    return translated
  }
  return typeof column.Header === 'string' ? column.Header : column.Title || column.id
}

/** Renders table header cell content (supports function/component headers). */
export const renderTableColumnHeader = (column: any) => {
  const translated = translateColumnHeader(column)
  if (translated) {
    return translated
  }

  if (typeof column.render === 'function') {
    return column.render('Header')
  }

  return getColumnHeaderText(column)
}

export const contactAccessor =
  ({ valuePath, verificationStatusPath, type }: { valuePath: string; verificationStatusPath: string; type: string }) =>
  (row: Row): { value: any; verified: boolean; type: string } => ({
    value: get(row, valuePath),
    verified: get(row, verificationStatusPath),
    type,
  })

export const openMailToInNewTab = (email: string, event: MouseEvent) => {
  window.open(`mailto:${email}`, 'mail')
  event.preventDefault()
}

export const styleCell = (Cell: React.FC<any>, className: string) => (props: CellProps<any>) =>
  <Cell {...props} className={className} />

export const styleDateCell = (className: string) => styleCell(DateCell, className)

export const styleTextCell = (className: string) => styleCell(NullableTextCell, className)

export const createStringSort = (accessor: string) => {
  return (rowA: any, rowB: any) => {
    const valueA = (rowA.values[accessor] || '').toLowerCase().trim()
    const valueB = (rowB.values[accessor] || '').toLowerCase().trim()

    return valueA.localeCompare(valueB)
  }
}

export const createTagsSort = (accessor: string) => {
  return (rowA: any, rowB: any) => {
    const valueA = map(rowA.values[accessor], 'name').join(',').toLowerCase().trim()
    const valueB = map(rowB.values[accessor], 'name').join(',').toLowerCase().trim()

    return valueA.localeCompare(valueB)
  }
}

export const getTableSort = (table: any): { id: string; desc: boolean } | undefined => {
  const column = table.columns.find((column: any) => column.isSorted)
  if (column) {
    return { id: column.id, desc: column.isSortedDesc }
  }
}

/** Insert columns missing from saved order at their definition-order positions (not appended at end). */
export const mergeColumnOrder = (definitionOrder: string[], savedOrder: string[]): string[] => {
  const merged = savedOrder.filter(id => definitionOrder.includes(id))

  for (const id of definitionOrder) {
    if (merged.includes(id)) {
      continue
    }

    const defIndex = definitionOrder.indexOf(id)
    let insertAt = merged.length
    for (let i = defIndex + 1; i < definitionOrder.length; i++) {
      const nextIdx = merged.indexOf(definitionOrder[i])
      if (nextIdx >= 0) {
        insertAt = nextIdx
        break
      }
    }
    merged.splice(insertAt, 0, id)
  }

  return merged
}
